<!-- Modal Body -->
<div class="modal-body hub-aux-modal">
  <div class="hub-aux-modal__close">
    <button
      type="button"
      class="btn-close"
      aria-label="Close"
      (click)="onCancel()"></button>
  </div>
  <div class="hub-cameras-title">Hub &amp; AUX Cameras</div>
  <!-- Device Summary Card -->
  <div
    class="device-data"
    *ngIf="metadata">
    <div class="subsection-pd">
      <div class="serial">{{ deviceSerial }}</div>
      <div class="label">VEHICLE (GROUP)</div>
    </div>
    <div class="subsection-pd">
      <div class="serial-subtitle">{{ devicePlatform }}</div>
      <div class="value group-value">{{ vehicleInfo || '--' }}</div>
    </div>
    <div class="subsection-pd-b">
      <div class="label">LAST COMMUNICATED</div>
      <div class="label">STATUS</div>
    </div>
    <div class="subsection-pd-b">
      <div class="value group-value value-b">
        {{ lastCommunicated | date: "EEE MMM d, y h:mmaaaaa'm'" }}
        <span class="days-message">
          {{ getRelativeTime(lastCommunicated) }}
        </span>
      </div>
      <div class="value group-value value-b">
        {{ operationalState || '--' }}
      </div>
    </div>
  </div>

  <!-- Hub and Aux Cameras Section Header -->
  <div class="camera-info__content">
    <div>
      <div class="hub-cameras-title">Hub and Aux Cameras</div>
      <div class="hub-serialnumber">{{ hubSerial }}</div>
    </div>

    <!-- Hub Last Communicated — stacked vertically -->
    <div class="last-updated-row">
      <span class="hub-comm-label">HUB LAST COMMUNICATED</span>
      <div class="hub-meta-row">
        <span class="hub-comm-value">
          {{ hubLastCommunicated | date: 'EEE, MMM d, yyyy h:mm:ss a' }}
        </span>
        <span class="hub-comm-relative">
          {{ getRelativeTime(hubLastCommunicated) }}
        </span>
        <div
          *ngIf="!isHubConnected()"
          class="hub-connection-status">
          <i class="lx-icon lx-circle dot not_connected"></i>
          <span class="not_connected">Not Connected</span>
        </div>
        <div class="request-new-images-group">
          <button
            type="button"
            class="request-new-images-link"
            [disabled]="requestingImages || !canRequestNewImage"
            (click)="onRequestNewImage()">
            <i
              class="lx-icon lx-reset"
              [class.spin]="requestingImages"></i>
            <span *ngIf="!requestingImages">Request New Images</span>
            <span *ngIf="requestingImages">Requesting image&hellip;</span>
          </button>
          <div
            *ngIf="liveImageError"
            class="request-new-images-error">
            {{ liveImageError }}
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Pending banner — shown while a previously triggered hub disassociation is still being confirmed by the backend -->
  <div
    *ngIf="hubDissociationPending"
    class="hub-pending-banner">
    <span class="warning-icon">&#8987;</span>
    Hub disassociation is in progress&hellip;
  </div>

  <!-- Tree Diagram -->
  <div class="container">
    <!-- Hub warning when selected — shown above checkbox -->
    <div
      *ngIf="selectHubDevice"
      class="hub-warning">
      <span class="warning-icon">&#9888;</span>
      Warning: Choosing a HUB will remove all AUX cameras from your setup.
    </div>

    <!-- Hub checkbox sits above the hub image -->
    <div
      class="hub-checkbox-wrapper hub-tooltip-wrapper"
      [class.hub-tooltip-active]="
        !!hubDisabledReason && !hubDissociationPending
      ">
      <input
        class="hub-aux-checkbox"
        id="hubDeviceCheck"
        type="checkbox"
        [checked]="selectHubDevice"
        [disabled]="
          hubDissociationPending || !canDissociate || !isDissociableState
        "
        (change)="onHubCheckChange($any($event.target).checked)" />
      <span
        *ngIf="hubDisabledReason"
        class="hub-tooltip-text">
        {{ hubDisabledReason }}
      </span>
    </div>

    <!-- Level 1: Hub Device -->
    <div class="level-1 rectangle">
      <img
        alt="Hub Device"
        height="158.24"
        src="../../../assets/images/hub-device.svg"
        width="131.71" />
      <div class="dot-connector"></div>
    </div>

    <!-- Level 2: AUX Camera Cards -->
    <div
      *ngIf="auxCameras.length"
      class="level-2-wrapper"
      [ngClass]="'grid-' + auxCameras.length">
      <ng-container *ngFor="let cam of auxCameras">
        <div class="level-2-item">
          <!-- Red x on the connecting arrow when the view has no signal -->
          <div
            *ngIf="showNoSignal(cam)"
            class="error-indicator"
            [title]="
              'Last recorded: ' + formatLastRecordedDate(cam.lastRecordedDate)
            ">
            x
          </div>

          <div
            class="camera-view image-wrapper"
            [class.error]="showNoSignal(cam)"
            [class.selected]="cam.selected && !selectHubDevice">
            <!-- Camera label header -->
            <div
              class="camera-header label-overlay image-title"
              [ngClass]="{ 'no-signal': showNoSignal(cam) }">
              {{ cam.label }}
            </div>

            <!-- Image only when one is available, the hub is reporting, and the
                 photo was refreshed AFTER the last recording. An image older
                 than the last recorded frame never overrides No Signal. -->
            <ng-container *ngIf="showImage(cam); else noImageTpl">
              <img
                class="camera-image"
                [src]="cam.photo?.url"
                [alt]="cam.label" />
            </ng-container>
            <ng-template #noImageTpl>
              <!-- No image but lastRecordedDate is fresh: the preview is just missing -->
              <div
                *ngIf="showNoPreview(cam); else noSignalTpl"
                class="status-box">
                No image available
              </div>
              <!-- Stale or absent dates, or a stale hub: No Signal -->
              <ng-template #noSignalTpl>
                <div class="status-box no-signal">No Signal</div>
              </ng-template>
            </ng-template>

            <!-- Camera footer with last refreshed date — only shown when image is actually rendered -->
            <div
              *ngIf="showImage(cam)"
              class="camera-footer label-overlay">
              Last refreshed: {{ formatLastRecordedDate(cam.photoSavedDate) }}
            </div>
          </div>

          <!-- Per-camera checkbox (always visible) -->
          <div
            class="camera-checkbox-wrapper hub-tooltip-wrapper"
            [class.hub-tooltip-active]="
              !!auxDisabledReason && !hubDissociationPending && !selectHubDevice
            ">
            <input
              type="checkbox"
              class="hub-aux-checkbox"
              [id]="'auxCam-' + cam.name"
              [checked]="cam.selected"
              [disabled]="
                selectHubDevice || hubDissociationPending || !canDissociate
              "
              (change)="onAuxCheckChange(cam, $any($event.target).checked)" />
            <span
              *ngIf="auxDisabledReason"
              class="hub-tooltip-text">
              {{ auxDisabledReason }}
            </span>
          </div>
        </div>
      </ng-container>
    </div>
  </div>
  <div
    *ngIf="imageCaptureDate"
    class="image-capture-row">
    <span class="image-capture-label">IMAGE CAPTURE</span>
    <span class="image-capture-value">{{ imageCaptureDate }}</span>
  </div>
</div>

<!-- Modal Footer -->
<div class="modal-footer">
  <div class="hub-aux-modal__instruction">
    Select a camera or the entire HUB device to disassociate it from the main
    event recorder.
  </div>
  <div class="modal-footer__actions">
    <button
      class="lx-btn lx-btn-secondary hub-aux-modal__cancel-btn"
      type="button"
      (click)="onCancel()">
      Cancel
    </button>
    <button
      *ngIf="canDissociate"
      class="lx-btn lx-btn-primary hub-aux-modal__disassociate-btn"
      type="button"
      [disabled]="isDisassociateDisabled"
      (click)="onDisassociate()">
      <span *ngIf="!isProcessing">{{ disassociateButtonLabel }}</span>
      <span *ngIf="isProcessing">Processing...</span>
    </button>
  </div>
</div>
